import { Eva2Error, ErrorTypes } from '~/lib/errors/Eva2Error';
import type { CategorySchema } from '~/types/schema';
import type { CategoryAssociatedIds } from '~/types/quiz';
import type { ContentTypes } from '~/types/adamTypes';

export type UseCategory = {
  contentType: ComputedRef<ContentTypes>;
  category: Ref<CategorySchema>;
  mediaIds: Ref<string[]>;
  associatedIds: Ref<CategoryAssociatedIds>;
  load: () => Promise<void>;
  unload: () => void;
};

export const INJECT_CATEGORY = Symbol(
  'useCategory composable'
) as InjectionKey<UseCategory>;

export const useCategory = (
  categoryGetParams: UseCategoryGetParams
): UseCategory => {
  const { category, mediaIds, associatedIds, fetch, fetchChildren, unload } =
    useCategoryPopulate();

  const load = async () => {
    const { isStudyContent, baseCategory, currentContent, slug, isHome } =
      categoryGetParams;
    let result;
    if (!baseCategory) {
      result = await fetch(
        {
          slug: slug.value,
          libraryBookId: currentContent.value.id,
        },
        isHome,
        true,
        true,
        isStudyContent
      );
      if (!result) {
        throw new Eva2Error(
          ErrorTypes.CategoryMissing,
          slug.value,
          `missing category with slug ${slug.value}`
        );
      }
    } else {
      result = loadChildren();
    }

    return result;
  };

  const loadChildren = async () => {
    const { isStudyContent, isHome, baseCategory, currentContent } =
      categoryGetParams;
    const libraryBookId = toValue(currentContent).id;
    const result = await fetchChildren(
      baseCategory,
      libraryBookId,
      isHome,
      true,
      true,
      isStudyContent
    );
    if (!result) {
      throw new Eva2Error(ErrorTypes.CategoryMissing);
    }
    return result;
  };

  const contentType = computed(() => category.value.contentType);

  return {
    contentType,
    category,
    mediaIds,
    associatedIds,
    load,
    unload,
  };
};
